// Google Calendar OAuth 2.0 + Calendar API
// Scope: https://www.googleapis.com/auth/calendar (Lesen + Schreiben)
// Authorization-Code-Flow mit PKCE. Google-OAuth-Clients vom Typ
// „Webanwendung" verlangen am Token-Endpoint ZUSÄTZLICH das Client-Secret
// (auch mit PKCE) — es wird lokal in der gcal_config gehalten und beim
// Token-Tausch mitgesendet. Einzelnutzer-App auf eigenem Gerät.
// Hinweis: Scope-Erweiterung ggü. früher (nur .readonly) — bestehende
// Verbindungen müssen einmal neu verbunden werden, damit Termine anlegbar
// sind.

const SCOPES      = 'https://www.googleapis.com/auth/calendar';
const REDIRECT_URI = window.location.origin + '/auth/google/callback';

// ── Typen ─────────────────────────────────────────────────────────────────

export interface GoogleEvent {
  id:       string;
  summary:  string;
  start:    { dateTime?: string; date?: string };
  end:      { dateTime?: string; date?: string };
  location?: string;
  description?: string;
  colorId?: string;
  calendarId?: string;   // Quell-Kalender (gesetzt beim Laden mehrerer Kalender)
}

export interface CalendarListEntry {
  id:      string;
  summary: string;
  colorId?: string;
  primary?: boolean;
}

export interface GoogleTokens {
  access_token:  string;
  expires_at:    number;   // ms timestamp
  refresh_token?: string;
}

const TOKEN_KEY = 'amed_google_tokens';

// ── Token-Verwaltung ──────────────────────────────────────────────────────

export function getTokens(): GoogleTokens | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    return raw !== null ? (JSON.parse(raw) as GoogleTokens) : null;
  } catch { return null; }
}

export function setTokens(t: GoogleTokens): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(t));
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export function isTokenValid(): boolean {
  const t = getTokens();
  return t !== null && t.expires_at > Date.now() + 60_000;
}

// ── PKCE-Hilfsfunktionen ──────────────────────────────────────────────────

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest('SHA-256', encoder.encode(plain));
}

function base64url(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function generateCodeVerifier(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return base64url(arr.buffer);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  return base64url(await sha256(verifier));
}

// ── OAuth-Flow ────────────────────────────────────────────────────────────

export async function startGoogleOAuth(clientId: string): Promise<void> {
  const verifier   = generateCodeVerifier();
  const challenge  = await generateCodeChallenge(verifier);
  const state      = base64url(crypto.getRandomValues(new Uint8Array(16)).buffer);

  sessionStorage.setItem('amed_oauth_verifier', verifier);
  sessionStorage.setItem('amed_oauth_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             clientId,
    redirect_uri:          REDIRECT_URI,
    scope:                 SCOPES,
    access_type:           'offline',
    prompt:                'consent',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

// Letzter Verbindungsfehler — wird von der Kalender-UI angezeigt, damit der
// Rücksprung nicht mehr stumm bei „nicht verbunden" endet.
const OAUTH_ERROR_KEY = 'amed_gcal_oauth_error';

export function getOAuthError(): string | null {
  return localStorage.getItem(OAUTH_ERROR_KEY);
}
export function clearOAuthError(): void {
  localStorage.removeItem(OAUTH_ERROR_KEY);
}
function setOAuthError(msg: string): void {
  localStorage.setItem(OAUTH_ERROR_KEY, msg);
}

export async function handleOAuthCallback(
  clientId: string,
  code: string,
  state: string,
  clientSecret?: string,
): Promise<boolean> {
  const savedState    = sessionStorage.getItem('amed_oauth_state');
  const codeVerifier  = sessionStorage.getItem('amed_oauth_verifier');

  if (state !== savedState || codeVerifier === null) {
    setOAuthError('Sicherheits-Token (state) fehlt oder stimmt nicht — bitte erneut verbinden. (Der Browser hat die Zwischendaten verloren, z. B. durch privaten Modus oder Neustart während der Anmeldung.)');
    return false;
  }

  sessionStorage.removeItem('amed_oauth_state');
  sessionStorage.removeItem('amed_oauth_verifier');

  const body: Record<string, string> = {
    grant_type:     'authorization_code',
    client_id:      clientId,
    redirect_uri:   REDIRECT_URI,
    code,
    code_verifier:  codeVerifier,
  };
  // „Webanwendung"-Clients verlangen bei Google zusätzlich das Client-Secret
  // am Token-Endpoint (auch mit PKCE). Ohne Secret → invalid_client.
  if (clientSecret !== undefined && clientSecret !== '') {
    body['client_secret'] = clientSecret;
  }

  let resp: Response;
  try {
    resp = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(body),
    });
  } catch (e) {
    setOAuthError(`Netzwerkfehler beim Token-Tausch: ${e instanceof Error ? e.message : String(e)}`);
    return false;
  }

  if (!resp.ok) {
    const raw = await resp.text().catch(() => '');
    let detail = raw.slice(0, 300);
    try {
      const j = JSON.parse(raw) as { error?: string; error_description?: string };
      if (j.error !== undefined) {
        detail = `${j.error}${j.error_description !== undefined ? ': ' + j.error_description : ''}`;
      }
    } catch { /* raw belassen */ }
    // Häufigster Fall: Web-Client ohne Secret.
    if (detail.includes('client_secret') || detail.includes('invalid_client')) {
      detail += ' — Tipp: Der OAuth-Client ist Typ „Webanwendung" und braucht zusätzlich das Client-Secret. Trage es in der Einrichtung ein.';
    }
    setOAuthError(`Token-Tausch abgelehnt (HTTP ${resp.status}): ${detail}`);
    return false;
  }

  clearOAuthError();

  const data = await resp.json() as {
    access_token: string;
    expires_in:   number;
    refresh_token?: string;
  };

  const tokens: GoogleTokens = {
    access_token: data.access_token,
    expires_at:   Date.now() + data.expires_in * 1000,
    ...(data.refresh_token !== undefined ? { refresh_token: data.refresh_token } : {}),
  };
  setTokens(tokens);

  return true;
}

// ── API-Aufrufe ───────────────────────────────────────────────────────────

async function apiFetch<T>(url: string): Promise<T> {
  const tokens = getTokens();
  if (tokens === null) throw new Error('Nicht angemeldet');

  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${tokens.access_token}` },
  });

  if (resp.status === 401) {
    clearTokens();
    throw new Error('Token abgelaufen — bitte neu anmelden');
  }
  if (!resp.ok) throw new Error(`API-Fehler ${resp.status}`);

  return resp.json() as Promise<T>;
}

export async function listCalendars(): Promise<CalendarListEntry[]> {
  const data = await apiFetch<{ items: CalendarListEntry[] }>(
    'https://www.googleapis.com/calendar/v3/users/me/calendarList'
  );
  return data.items;
}

export async function listEvents(
  calendarId: string,
  timeMin: string,
  timeMax: string,
  maxResults = 50,
): Promise<GoogleEvent[]> {
  const params = new URLSearchParams({
    timeMin,
    timeMax,
    maxResults:   String(maxResults),
    singleEvents: 'true',
    orderBy:      'startTime',
  });

  const data = await apiFetch<{ items: GoogleEvent[] }>(
    `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params.toString()}`
  );

  return data.items;
}

// Mehrere Kalender kombiniert laden: pro Kalender parallel, Events mit ihrem
// Quell-Kalender markiert, zusammengeführt und nach Startzeit sortiert.
// Einzelne nicht ladbare Kalender werden übersprungen; nur wenn ALLE scheitern,
// wird der erste Fehler geworfen.
export async function listEventsMulti(
  calendarIds: string[],
  timeMin: string,
  timeMax: string,
  maxResults = 50,
): Promise<GoogleEvent[]> {
  if (calendarIds.length === 0) return [];

  const settled = await Promise.allSettled(
    calendarIds.map(id =>
      listEvents(id, timeMin, timeMax, maxResults)
        .then((evs): GoogleEvent[] => evs.map(ev => ({ ...ev, calendarId: id })))
    )
  );

  const ok = settled
    .filter((r): r is PromiseFulfilledResult<GoogleEvent[]> => r.status === 'fulfilled')
    .flatMap(r => r.value);

  if (ok.length === 0) {
    const firstErr = settled.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (firstErr !== undefined) {
      throw firstErr.reason instanceof Error ? firstErr.reason : new Error(String(firstErr.reason));
    }
    return [];
  }

  ok.sort((a, b) => {
    const sa = a.start.dateTime ?? a.start.date ?? '';
    const sb = b.start.dateTime ?? b.start.date ?? '';
    return sa.localeCompare(sb);
  });
  return ok;
}

export async function listTodayEvents(calendarId = 'primary'): Promise<GoogleEvent[]> {
  const now   = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const end   = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
  return listEvents(calendarId, start, end);
}

// ── Schreiben (Termine anlegen / ändern / löschen) ────────────────────────

async function apiWrite<T>(url: string, method: string, body?: unknown): Promise<T | null> {
  const tokens = getTokens();
  if (tokens === null) throw new Error('Nicht angemeldet');

  const resp = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${tokens.access_token}`,
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (resp.status === 401) {
    clearTokens();
    throw new Error('Token abgelaufen — bitte neu anmelden');
  }
  if (resp.status === 403) {
    throw new Error('Kein Schreibrecht — bitte Google Calendar einmal neu verbinden (Freigabe erweitern).');
  }
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(`API-Fehler ${resp.status}: ${t.slice(0, 200)}`);
  }
  if (resp.status === 204) return null;
  return resp.json() as Promise<T>;
}

export interface EventInput {
  summary:     string;
  startISO:    string;   // ISO mit Uhrzeit
  endISO:      string;
  description?: string;
  location?:   string;
}

function toRequestBody(input: EventInput): Record<string, unknown> {
  return {
    summary: input.summary,
    ...(input.description !== undefined && input.description !== '' ? { description: input.description } : {}),
    ...(input.location !== undefined && input.location !== '' ? { location: input.location } : {}),
    start: { dateTime: new Date(input.startISO).toISOString() },
    end:   { dateTime: new Date(input.endISO).toISOString() },
  };
}

export async function createEvent(calendarId: string, input: EventInput): Promise<GoogleEvent> {
  const data = await apiWrite<GoogleEvent>(
    `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`,
    'POST', toRequestBody(input),
  );
  return data as GoogleEvent;
}

export async function updateEvent(calendarId: string, eventId: string, input: EventInput): Promise<GoogleEvent> {
  const data = await apiWrite<GoogleEvent>(
    `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`,
    'PATCH', toRequestBody(input),
  );
  return data as GoogleEvent;
}

export async function deleteEvent(calendarId: string, eventId: string): Promise<void> {
  await apiWrite(
    `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`,
    'DELETE',
  );
}
